import { CURRENCY, FACTIONS, SHIP_CLASSES, TRADE } from '@sd/content';
import {
  capitalHex,
  caravanCargo,
  commandBlocker,
  contractSlots,
  involves,
  type Command,
  type GameState,
  type PlayerState,
} from '@sd/sim';
import type { Bot } from './match';
import { consumptionPerCycle } from './planner';

/** Deliveries a bot offers in one contract, and the share of its surplus per delivery. */
const DELIVERIES = 5;
/** A buyer accepts up to this multiple of the market price; a seller asks the market price. */
const MAX_PRICE_RATIO = 1.1;
/** ГС the bot keeps when buying a transport for a caravan. */
const CUSHION = 500;

/** Units of `good` a player is short of per cycle: what it uses beyond what it makes. */
function shortfall(p: PlayerState, good: keyof PlayerState['goods']): number {
  const use = consumptionPerCycle(p)[good];
  const made = p.planet.lastCycle?.produced[good] ?? 0;
  return Math.max(0, use - made);
}

const caravanFleets = (state: GameState) =>
  new Set(state.contracts.flatMap((c) => (c.caravan ? [c.caravan.fleetId] : [])));

/**
 * Phase 2 contractor (GDD §6): sells the faction's cheap good by contract to
 * a player who is short of it, accepts fair offers for goods it lacks, and
 * runs a caravan for every contract it sells on.
 */
export const contractorBot: Bot = (state, playerId, rng) => {
  if (state.pause || !rng.chance(0.03)) return [];
  const me = state.players.find((p) => p.id === playerId);
  if (!me) return [];
  const slots = contractSlots(me.planet);
  if (slots === 0) return [];
  const mine = state.contracts.filter((c) => involves(c, playerId));

  // 1. Accept fair offers addressed to me.
  for (const c of mine) {
    if (c.status !== 'proposed' || c.proposedBy === playerId) continue;
    if (mine.filter((x) => x.status === 'active').length >= slots) break;
    const market = state.market[c.good].price;
    const fair = c.price !== null && c.price <= market * MAX_PRICE_RATIO;
    const wanted =
      c.buyer === playerId &&
      (shortfall(me, c.good) > 0 || FACTIONS[me.faction].needs.includes(c.good as never));
    const canSell = c.seller === playerId && me.goods[c.good] >= c.quantity * 2;
    const accept: Command = { type: 'acceptContract', playerId, contractId: c.id };
    // Wars, embargoes and sanctions can close a deal (#64): no asking for what the rules refuse.
    if (fair && (wanted || canSell) && !commandBlocker(state, accept)) return [accept];
  }

  // 2. Give every contract I sell on a caravan: an idle transport, or build one.
  const busy = caravanFleets(state);
  const waiting = mine.find((c) => c.status === 'active' && c.seller === playerId && !c.caravan);
  if (waiting) {
    const idle = state.fleets.find(
      (f) =>
        f.owner === playerId &&
        !busy.has(f.id) &&
        f.ships.every((s) => SHIP_CLASSES[s.cls].cargo > 0) &&
        caravanCargo(state, waiting, f) >= waiting.quantity,
    );
    if (idle)
      return [{ type: 'assignCaravan', playerId, contractId: waiting.id, fleetId: idle.id }];
    const ship = me.faction === 'earthborn' ? 'tradeLiner' : 'transport';
    if (me.shipyard.some((o) => o.ship === ship)) return [];
    const gsCash = (me.cash * me.currency.rate) / (1 + CURRENCY.conversionFee);
    if (gsCash < SHIP_CLASSES[ship].cost.cash + CUSHION) return [];
    if (me.goods.alloys < SHIP_CLASSES[ship].cost.alloys) return [];
    return [{ type: 'buildShip', playerId, ship } satisfies Command];
  }

  // 3. Offer my cheap good to whoever is short of it.
  if (
    mine.length >= slots ||
    mine.some((c) => c.proposedBy === playerId && c.status === 'proposed')
  ) {
    return [];
  }
  const good = FACTIONS[me.faction].cheapGood;
  const spare = me.goods[good] - consumptionPerCycle(me)[good] * 6;
  const quantity = Math.min(40, Math.floor(spare / DELIVERIES));
  if (quantity < 5) return [];
  const buyers = state.players
    .filter((p) => p.id !== playerId && contractSlots(p.planet) > 0)
    .filter((p) => !mine.some((c) => involves(c, p.id)))
    .filter((p) => shortfall(p, good) > 0 || FACTIONS[p.faction].needs.includes(good))
    .sort(
      (a, b) =>
        Math.abs(capitalHex(state, a.id).q - capitalHex(state, playerId).q) -
          Math.abs(capitalHex(state, b.id).q - capitalHex(state, playerId).q) ||
        a.id.localeCompare(b.id),
    );
  const price = Math.round(state.market[good].price * 100) / 100;
  const offer = (other: string): Command => ({
    type: 'proposeContract',
    playerId,
    other,
    role: 'sell',
    good,
    quantity,
    price,
    deliveries: Math.min(DELIVERIES, TRADE.maxDeliveries),
  });
  const buyer = buyers.find((p) => !commandBlocker(state, offer(p.id)));
  return buyer ? [offer(buyer.id)] : [];
};
